// src/systems/monitoring/features/gis-view/components/ui/PanelCard.tsx
import type { ReactNode } from 'react'

interface PanelCardProps {
  title: string
  subtitle?: string
  /** Optional leading icon, shown in a translucent tile on the navy header. */
  icon?: ReactNode
  /** Right-aligned header slot (Reset all / view toggle + Export). */
  action?: ReactNode
  /** When true the body is a flex column that fills height (for the scrolling list card). */
  fillBody?: boolean
  className?: string
  children: ReactNode
}

/**
 * The screenshot's card: a navy-gradient header (title + optional subtitle + right action slot) over a
 * white body. Used across the GIS dashboard cards so their chrome stays DRY.
 */
export function PanelCard({ title, subtitle, icon, action, fillBody, className, children }: PanelCardProps) {
  return (
    <section
      className={`flex min-h-0 flex-col overflow-hidden rounded-[12px] border border-gis-line bg-gis-card shadow-gis-card ${className ?? ''}`}
    >
      <header
        className="relative flex items-center gap-2.5 overflow-hidden px-3.5 py-[6px] text-white"
        style={{ background: 'linear-gradient(120deg, #1e2a52, #2a3b7d)' }}
      >
        {/* Decorative splash art — recolored to the ink-blue subheading tone, right-anchored, faded left. */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-y-0 right-0 w-1/2 select-none [mask-image:linear-gradient(to_right,transparent,black_55%)] [-webkit-mask-image:linear-gradient(to_right,transparent,black_55%)]"
        >
          <div className="h-full w-full bg-gis-subheading [mask-image:url('/image/splash_art.png')] [mask-position:right] [mask-repeat:no-repeat] [mask-size:cover] [-webkit-mask-image:url('/image/splash_art.png')] [-webkit-mask-position:right] [-webkit-mask-repeat:no-repeat] [-webkit-mask-size:cover]" />
        </div>
        {icon && (
          <span className="relative z-10 grid h-[22px] w-[22px] flex-none place-items-center rounded-[8px] bg-white/12 text-white">
            {icon}
          </span>
        )}
        <div className="relative z-10 min-w-0">
          <h2 className="truncate text-[13.5px] font-bold tracking-[-.01em] text-white">{title}</h2>
          {subtitle && <p className="truncate text-[10.5px] text-white/70">{subtitle}</p>}
        </div>
        {action && <div className="relative z-10 ml-auto flex flex-none items-center gap-2">{action}</div>}
      </header>
      <div className={fillBody ? 'flex min-h-0 flex-1 flex-col' : ''}>{children}</div>
    </section>
  )
}
